/* eslint-disable react/no-array-index-key */
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
/* import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight"; */
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronLeft from "@sunSource/Icons/ChevronLeft";
import ChevronRight from "@sunSource/Icons/ChevronRight";
import * as cssLinter from "css";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import React, { FC, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface LogoItem {
    image: string;
    title: string;
}


interface SlideModel {
    fields: {
        slideTitle: string;
        background: "image" | "color";
        image: string;
        imageOverlay: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        heading: string;
        subheading: string;
        description: string;
        footerTitle: string;
        logos?: LogoItem[];
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        focalPoint:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        contentPadding: number;
        centerTextVertically: boolean;
        slidTextAlignment: "left" | "center" | "right";
        openLinkInNewTab: boolean;
        customCSS: string;
    };
}

const enum fields {
    height = "height",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
    logos = "logos"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
        [fields.logos]: LogoItem[];
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
}

export const slideshowStyles: SlideshowStyles = {
    slideshowWrapper: {
        css: css`
            padding: 0px 0;
            position: relative;
            font-family: Barlow-Regular;
            svg polyline {
                stroke: #fff;
                stroke-width: 5px;
            }
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -40px;
            left: 1.5%;
        `,
    },
    prevArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
                border: 0 !important;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    iconProps: {
        size: 35,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: white;
            background-repeat: no-repeat;
            background-position: center;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
            margin-top: 36px;
            border-radius: 3px;
            padding: 0px 17.5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 23px;
                            background-color: #fff;
                            border: none;
                            padding: 0px 17.5px;
                            &:hover {
                                background-color: #fff;
                            }
                            span {
                                font-size: 12px;
                                font-weight: 600;
                                letter-spacing: 2px;
                                line-height: 20px;
                                text-align: center;
                                color: #33333a;
                                padding: 10px 18.5px;
                                text-transform: uppercase;
                                &:hover {
                                    color: #006bc0;
                                }
                            }
                        `,
                        css`
                            margin-top: 23px;
                            background-color: #fff;
                            border: none;
                            padding: 0px 17.5px;
                            &:hover {
                                background-color: #fff;
                            }
                            span {
                                font-size: 12px;
                                font-weight: 600;
                                letter-spacing: 2px;
                                line-height: 20px;
                                text-align: center;
                                color: #33333a;
                                padding: 10px 18.5px;
                                text-transform: uppercase;
                                &:hover {
                                    color: #006bc0;
                                }
                            }
                        `,
                        css`
                            margin-top: 36px;
                            background-color: #fff;
                            border: none;
                            padding: 0px 17.5px;
                            &:hover {
                                background-color: #fff;
                            }
                            span {
                                font-size: 12px;
                                font-weight: 600;
                                letter-spacing: 2px;
                                line-height: 20px;
                                text-align: center;
                                color: #33333a;
                                padding: 10px 18.5px;
                                text-transform: uppercase;
                                &:hover {
                                    color: #006bc0;
                                }
                            }
                        `,
                        css`
                            margin-top: 36px;
                            background-color: #2e64b0;
                            border: none;
                            padding: 0px 17.5px;
                            &:hover {
                                background-color: #fff;
                            }
                            span {
                                font-size: 12px;
                                font-weight: 600;
                                letter-spacing: 2px;
                                line-height: 20px;
                                text-align: center;
                                color: #fff !important;
                                padding: 10px 18.5px;
                                text-transform: uppercase;
                                &:hover {
                                    color: #2e64b0 !important;
                                }
                            }
                        `,
                        css`
                            margin-top: 36px;
                            background-color: #2e64b0;
                            border: none;
                            padding: 0px 17.5px;
                            &:hover {
                                background-color: #fff;
                            }
                            span {
                                font-size: 12px;
                                font-weight: 600;
                                letter-spacing: 2px;
                                line-height: 20px;
                                text-align: center;
                                color: #fff !important;
                                padding: 10px 18.5px;
                                text-transform: uppercase;
                                &:hover {
                                    color: #2e64b0 !important;
                                }
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 22px;

            margin-bottom: 18px;
            //position: absolute;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 23px;
                        `,
                        css`
                            margin-top: 23px;
                        `,
                        css`
                            margin-top: -22px;
                            position: relative;
                        `,
                        css`
                            margin-top: -22px;
                            position: relative;
                        `,
                        css`
                            margin-top: -22px;
                            position: relative;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    dotButton: {
        css: css`
            // height: 3px;
            // width: 30px;
            height: 5px;
            width: 40px;
            min-height: 0px;
            background-color: #cccccc;
            padding: 0;
            margin: 0 5px;
            border: 0;
            //background: ${getColor("common.border")};
            &:hover {
                background-color: rgb(36, 36, 41);
            }
            &:active {
                background: #006bc0;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -40px;
            right: 1.5%;
        `,
    },
    nextArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
                border: 0 !important;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    slideCenteringWrapperStyles:{
        css:css`
            position: relative;
            z-index: 1;
            padding: 20px;
            &::before{
                content: "";
                height: 100%;
                width: 100%;
                background: #0056a0;
                position: absolute;
                left: 0px;
                top: 0px;
                z-index: -1;
                opacity: 0.9;
            }
        `
    },
    subheadingText:{
        css: css`
            text-align: left;
        `
    }
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}
`;

export const Slideshow: FC<OwnProps> = ({ fields, extendedStyles }) => {
  
    let logos = (fields.logos || []).map(item =>
        (item as any).fields ? (item as any).fields : item
    );
    const { isInShell } = useShellContext();
    const history = useHistory();
    const buttonLinks = useGetLinks(fields.slides, o => o.fields.buttonLink);
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, fields.slides]);

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };
    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    let heightStyles: string;
    switch (fields.height) {
        case "1/4 viewport":
            heightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            heightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            heightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            heightStyles = "min-height: 100vh;";
            break;
    }

    const textAlignStyles = `text-align: ${fields.textAlignment};`;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...styles.customCssWrapper}>
            <StyledWrapper className="slideshow-wrapper" {...styles.slideshowWrapper}>
                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper {...styles.prevArrowWrapper}>
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            aria-label={translate("Previous")}
                        >
                            <ButtonIcon {...styles.iconProps} src={ChevronLeft} />
                        </Button>
                    </StyledWrapper>
                )}
                <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                    <StyledWrapper className="slideshow-slide-container" {...styles.slideContainerWrapper}>
                        {fields.slides.map((slide, index:any) => {
                            const buttonLink = buttonLinks.links[index];

                            let responsiveImageBehaviorStyles;
                            if (slide.fields.responsiveImageBehavior === "cover") {
                                responsiveImageBehaviorStyles = "background-size: cover;";
                            } else if (slide.fields.responsiveImageBehavior === "center") {
                                responsiveImageBehaviorStyles = "background-size: contain;";
                            } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                                responsiveImageBehaviorStyles = "background-size: auto 100%;";
                            } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                                responsiveImageBehaviorStyles = "background-size: 100% auto;";
                            }

                            const backgroundStyles =
                                slide.fields.background === "image"
                                    ? `background-image: url(${parseImageUrl(slide.fields.image).url});`
                                    : `background-color: ${slide.fields.backgroundColor};`;
                            const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);

                            let overlayPositioningStyles;
                            if (!slide.fields.partialOverlay) {
                                overlayPositioningStyles = "align-items: stretch;";
                            } else if (slide.fields.partialOverlayPositioning === "top") {
                                overlayPositioningStyles = "align-items: flex-start;";
                            } else if (slide.fields.partialOverlayPositioning === "middle") {
                                overlayPositioningStyles = "align-items: center;";
                            } else if (slide.fields.partialOverlayPositioning === "bottom") {
                                overlayPositioningStyles = "align-items: flex-end;";
                            }

                            let fontSizeStyles;
                            if (fields.responsiveFontSizes || fields.customFontSizes) {
                                fontSizeStyles = responsiveStyleRules(
                                    fields.responsiveFontSizes,
                                    fields.customFontSizes ? fields : undefined,
                                );
                            }

                            const slideWrapperStyles = {
                                css: css`
                                    ${styles.slideContentWrapper?.css || ""}
                                    ${({ theme }: { theme: BaseTheme }) =>
                                        breakpointMediaQueries(
                                            theme,
                                            [
                                                css`
                                                    min-height: 388px;
                                                `,
                                                css`
                                                    min-height: 388px;
                                                `,
                                                css`
                                                    min-height: 530px;
                                                `,
                                                css`
                                                    min-height: 530px;
                                                `,
                                                css`
                                                    ${heightStyles}
                                                `,
                                            ],
                                            "min",
                                        )}
                                
                                ${textAlignStyles}
                                ${backgroundStyles}
                                ${responsiveImageBehaviorStyles}
                                ${focalPointStyles}
                                ${overlayPositioningStyles}
                                ${fontSizeStyles}
                                `,
                            };
                            const slideOverlayWrapperStyles = {
                                css: css`
                                    ${styles.slideOverlayWrapper?.css || ""}
                                    background-color: ${slide.fields.background === "image"
                                        ? slide.fields.imageOverlay
                                        : ""};
                                    padding: ${slide.fields.contentPadding}px;
                                    display: flex;
                                    justify-content: ${slide.fields.slidTextAlignment};

                                    ${slide.fields.centerTextVertically &&
                                    `
                                    align-items: center;
                                    `}
                                    max-width: 1140px;
                                    ${({ theme }: { theme: BaseTheme }) =>
                                        breakpointMediaQueries(
                                            theme,
                                            [
                                                css`
                                                    max-width: 100% !important;
                                                    background-color: rgba(37, 37, 37, 0.7);
                                                    justify-content: flex-start;
                                                    background-position: center;
                                                `,
                                                css`
                                                    max-width: 100% !important;
                                                    background-color: rgba(37, 37, 37, 0.7);
                                                    justify-content: flex-start;
                                                    background-position: center;
                                                `,
                                                css`
                                                    background-position: center;
                                                    max-width: 720px !important;
                                                    background-color: transparent;
                                                    justify-content: flex-start;
                                                `,
                                                css`
                                                    max-width: 850px !important;
                                                    background-color: transparent;
                                                    justify-content: ${slide.fields.slidTextAlignment};
                                                `,
                                                css`
                                                    max-width: 1280px !important;
                                                    background-color: transparent;
                                                    justify-content: ${slide.fields.slidTextAlignment};
                                                `,
                                            ],
                                            "min",
                                        )}
                                    margin: 0 auto;
                                `,
                            };
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <StyledWrapper className="slide-wrapper" key={index} {...slideWrapperStyles}>
                                    <StyledWrapper {...slideOverlayWrapperStyles}>
                                        <StyledWrapper
                                            css={css`
                                                width: 45%;
                                                ${({ theme }: { theme: BaseTheme }) =>
                                                    breakpointMediaQueries(
                                                        theme,
                                                        [
                                                            css`
                                                                width: 100%;
                                                                padding: 30px;
                                                            `,
                                                            css`
                                                                width: 100%;
                                                                padding: 30px;
                                                            `,
                                                            css`
                                                                width: 50%;
                                                                padding: 22px;
                                                            `,
                                                            css`
                                                                width: 45%;
                                                                padding: 0px;
                                                            `,
                                                            css`
                                                                width: 45%;
                                                                padding: 0px;
                                                            `,
                                                        ],
                                                        "min",
                                                    )}
                                            `}
                                        >
                                            <StyledWrapper {...styles.slideCenteringWrapperStyles}>
                                                
                                                {slide.fields.subheading && (
                                                    <Typography
                                                        {...styles.subheadingText}
                                                        className="slide-subheading"
                                                        css={css`
                                                            p {
                                                                text-align: left;
                                                                color: #ffffff !important;
                                                                font-size: 1.5rem !important;
                                                                font-weight: 200 !important;
                                                                letter-spacing: 0 !important;
                                                                line-height: 53px !important;
                                                                margin-bottom: 15px !important;
                                                                ${({ theme }: { theme: BaseTheme }) =>
                                                                    breakpointMediaQueries(
                                                                        theme,
                                                                        [
                                                                            css`
                                                                                font-size: 1.5rem !important;
                                                                                font-weight: 200 !important;
                                                                                letter-spacing: 0 !important;
                                                                                line-height: 36px !important;
                                                                            `,
                                                                            css`
                                                                                font-size: 1.5rem !important;
                                                                                font-weight: 200 !important;
                                                                                letter-spacing: 0 !important;
                                                                                line-height: 32px !important;
                                                                            `,
                                                                            css`
                                                                                color: #ffffff !important;
                                                                                font-size: 1.5rem !important;
                                                                                font-weight: 200 !important;
                                                                                letter-spacing: 0 !important;
                                                                                line-height: 30px !important;
                                                                                margin-bottom: 15px !important;
                                                                            `,
                                                                            css`
                                                                                color: #ffffff !important;
                                                                                font-size: 1.5rem !important;
                                                                                font-weight: 200 !important;
                                                                                letter-spacing: 0 !important;
                                                                                line-height: 30px !important;
                                                                                margin-bottom: 15px !important;
                                                                            `,
                                                                            css`
                                                                                color: #ffffff !important;
                                                                                font-size: 1.5rem !important;
                                                                                font-weight: 200 !important;
                                                                                letter-spacing: 0 !important;
                                                                                line-height: 30px !important;
                                                                                margin-bottom: 15px !important;
                                                                            `,
                                                                        ],
                                                                        "min",
                                                                    )}
                                                            }
                                                        `}
                                                    >
                                                        {parse(slide.fields.subheading, parserOptions)}
                                                    </Typography>
                                                )}
                                                {slide.fields.heading && (
                                                    <Typography
                                                        {...styles.headingText}
                                                        className="slide-heading"
                                                        css={css`
                                                            p {
                                                                text-align: left;
                                                                font-size: 18px !important;
                                                                font-weight: bold !important;
                                                                letter-spacing: 1.08px !important;
                                                                line-height: 25px !important;
                                                                margin-bottom: 15px !important;
                                                                ${({ theme }: { theme: BaseTheme }) =>
                                                                    breakpointMediaQueries(
                                                                        theme,
                                                                        [
                                                                            css`
                                                                                font-size: 14px !important;
                                                                                font-weight: bold !important;
                                                                                letter-spacing: 0.84px !important;
                                                                                line-height: 17px !important;
                                                                            `,
                                                                            css`
                                                                                font-size: 18px !important;
                                                                                font-weight: bold !important;
                                                                                letter-spacing: 0.84px !important;
                                                                                line-height: 22px !important;
                                                                            `,
                                                                            css`
                                                                                font-size: 2.5rem !important;
                                                                                font-weight: bold !important;
                                                                                letter-spacing: 1.08px !important;
                                                                                line-height: 38px !important;
                                                                                margin-bottom: 15px !important;
                                                                            `,
                                                                            css`
                                                                                font-size: 2.5rem !important;
                                                                                font-weight: bold !important;
                                                                                letter-spacing: 1.08px !important;
                                                                                line-height: 41px !important;
                                                                                margin-bottom: 15px !important;
                                                                            `,
                                                                            css`
                                                                                font-size: 2.5rem !important;
                                                                                font-weight: bold !important;
                                                                                letter-spacing: 1.08px !important;
                                                                                line-height: 41px !important;
                                                                                margin-bottom: 15px !important;
                                                                            `,
                                                                        ],
                                                                        "min",
                                                                    )}
                                                            }
                                                        `}
                                                    >
                                                        {parse(slide.fields.heading, parserOptions)}
                                                    </Typography>
                                                )}
                                                {slide.fields.description && (
                                                    <Typography
                                                        {...styles.subheadingText}
                                                        className="slide-subheading"
                                                        css={css`
                                                            p {
                                                                text-align: left;
                                                                color: #ffffff !important;
                                                                font-size: 44px !important;
                                                                font-weight: 200 !important;
                                                                margin-bottom: 0 !important;
                                                                font-family: SourceSans3-Regular;
                                                                ${({ theme }: { theme: BaseTheme }) =>
                                                                    breakpointMediaQueries(
                                                                        theme,
                                                                        [
                                                                            css`
                                                                                font-size: 16px !important;
                                                                                font-weight: 200 !important;
                                                                            `,
                                                                            css`
                                                                                font-size: 20px !important;
                                                                                font-weight: 200 !important;
                                                                            `,
                                                                            css`
                                                                                color: #ffffff !important;
                                                                                font-size: 15px !important;
                                                                                font-weight: 200 !important;
                                                                                margin-bottom: 0 !important;
                                                                            `,
                                                                            css`
                                                                                color: #ffffff !important;
                                                                                font-size: 15px !important;
                                                                                font-weight: 200 !important;
                                                                                margin-bottom: 0 !important;
                                                                            `,
                                                                            css`
                                                                                color: #ffffff !important;
                                                                                font-size: 15px !important;
                                                                                font-weight: 200 !important;
                                                                                margin-bottom: 0 !important;
                                                                            `,
                                                                        ],
                                                                        "min",
                                                                    )}
                                                            }
                                                        `}
                                                    >
                                                        {parse(slide.fields.description, parserOptions)}
                                                    </Typography>
                                                )}
                                                {slide.fields.footerTitle && (
                                                    <Typography
                                                        css={css`
                                                            justify-content: left !important;
                                                            display: flex;
                                                            font-size: 1.5rem;
                                                            span{ 
                                                                text-align: left !important;
                                                            }
                                                            p {
                                                                font-size: 18px !important;
                                                                font-weight: bold !important;
                                                                letter-spacing: 1.08px !important;
                                                                line-height: 25px !important;
                                                                margin-bottom: 15px !important;
                                                                ${({ theme }: { theme: BaseTheme }) =>
                                                                    breakpointMediaQueries(
                                                                        theme,
                                                                        [
                                                                            css`
                                                                                font-size: 14px !important;
                                                                                font-weight: bold !important;
                                                                                letter-spacing: 0.84px !important;
                                                                                line-height: 17px !important;
                                                                            `,
                                                                            css`
                                                                                font-size: 18px !important;
                                                                                font-weight: bold !important;
                                                                                letter-spacing: 0.84px !important;
                                                                                line-height: 22px !important;
                                                                            `,
                                                                            css`
                                                                                font-size: 26px !important;
                                                                                font-weight: bold !important;
                                                                                letter-spacing: 1.08px !important;
                                                                                line-height: 38px !important;
                                                                                margin-bottom: 15px !important;
                                                                            `,
                                                                            css`
                                                                                font-size: 36px !important;
                                                                                font-weight: bold !important;
                                                                                letter-spacing: 1.08px !important;
                                                                                line-height: 41px !important;
                                                                                margin-bottom: 15px !important;
                                                                            `,
                                                                            css`
                                                                                font-size: 36px !important;
                                                                                font-weight: bold !important;
                                                                                letter-spacing: 1.08px !important;
                                                                                line-height: 41px !important;
                                                                                margin-bottom: 15px !important;
                                                                            `,
                                                                        ],
                                                                        "min",
                                                                    )}
                                                            }
                                                        `}
                                                    >
                                                        {parse(slide.fields.footerTitle, parserOptions)}
                                                    </Typography>
                                                )}
                                                {logos && logos.length > 0 && (
                                                    <StyledWrapper
                                                        css={css`
                                                            display: flex;
                                                            flex-wrap: wrap;
                                                            gap: 3px;
                                                            justify-content: center;
                                                            margin-top: 15px;
                                                        `}
                                                    >
                                                        {logos.map((logo, idx) => {
                                                            if(index+1 === Number(logo.slideNumber)){
                                                                return <StyledWrapper
                                                                    key={idx}
                                                                    css={css`
                                                                        display: flex;
                                                                        flex-direction: column;
                                                                        align-items: center;
                                                                        width: 95px;
                                                                    `}
                                                                >
                                                                    <img
                                                                        src={logo.image}
                                                                        alt={logo.title}
                                                                        style={{ width: "50px", height: "50px", objectFit: "contain", marginBottom: "0px" }}
                                                                    />
                                                                    <Typography
                                                                        css={css`
                                                                            font-size: 10px;
                                                                            color: #ffffff;
                                                                            text-align: center;
                                                                            margin-top: 0px;
                                                                        `}
                                                                    >
                                                                        {logo.title}
                                                                    </Typography>
                                                                </StyledWrapper>
                                                            }
                                                        }
                                                            
                                                    )}
                                                    </StyledWrapper>
                                                )}
                                                {/* {(slide.fields.buttonLabel || slide.fields.buttonLink.value) && (
                                                    <Link
                                                        {...styles.slideButton}
                                                        href={buttonLink?.url}
                                                        className="slide-button"
                                                        target={slide.fields.openLinkInNewTab ? "_blank" : ""}
                                                    >
                                                        {slide.fields.buttonLabel ||
                                                            buttonLink?.title ||
                                                            buttonLink?.url}
                                                    </Link>
                                                )} */}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
                {fields.slideIndicator && (
                    <StyledWrapper {...styles.dotsContainerWrapper} className="slide-indicator">
                        {fields.slides.map((_, i) => {
                            const dotColorStyles =
                                i === selectedIndex
                                    ? `
                                background: #006BC0;
                                    
                                    &:hover { background: #33333A; }
                                    &:active {
                                        background: #006BC0 ;
                                    }
                                   
                                `
                                    : "";
                            const dotButtonStyles = {
                                css: css`
                                    ${styles.dotButton?.css || ""}
                                    ${dotColorStyles}
                                `,
                            };
                            // eslint-disable-next-line react/no-array-index-key
                            return (
                                <Button
                                    key={i}
                                    {...dotButtonStyles}
                                    onClick={() => embla && embla.scrollTo(i)}
                                    aria-label={translate("Scroll to {0}", `${i + 1}`)}
                                    className="slide-dot-button"
                                />
                            );
                        })}
                    </StyledWrapper>
                )}
                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper {...styles.nextArrowWrapper} className="slide-arrow-wrapper">
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            aria-label={translate("Next")}
                            className="slide-arrow"
                        >
                            <ButtonIcon {...styles.iconProps} src={ChevronRight} />
                        </Button>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Image Color Overlay",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "partialOverlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "partialOverlayPositioning",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top", value: "top" },
                            { displayName: "Middle", value: "middle" },
                            { displayName: "Bottom", value: "bottom" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "bottom",
                        isVisible: widget => widget.fields.partialOverlay,
                    },
                    {
                        name: "responsiveImageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Prioritize height", value: "prioritizeHeight" },
                            { displayName: "Prioritize width", value: "prioritizeWidth" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "cover",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    {
                        name: "focalPoint",
                        displayName: "Focal Point",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top Left", value: "topLeft" },
                            { displayName: "Top Center", value: "topCenter" },
                            { displayName: "Top Right", value: "topRight" },
                            { displayName: "Center Left", value: "centerLeft" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Center Right", value: "centerRight" },
                            { displayName: "Bottom Left", value: "bottomLeft" },
                            { displayName: "Bottom Center", value: "bottomCenter" },
                            { displayName: "Bottom Right", value: "bottomRight" },
                        ],
                        defaultValue: "center",
                        hideEmptyOption: true,
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "description",
                        editorTemplate: "RichTextField",
                        displayName: "Description",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "footerTitle",
                        editorTemplate: "TextField",
                        displayName: "Footer Title",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "openLinkInNewTab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
		                name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                    },
                    {
                        name: "centerTextVertically",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "slidTextAlignment",
                        editorTemplate: "DropDownField",
                        defaultValue: "left",
                        options: [
                            { displayName: "Left", value: "flex-start" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Right", value: "flex-end" },
                        ],
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,

                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });

                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: "logos",
                displayName: "Logos",
                editorTemplate: "ListField",
                fieldType: "Translatable",
                getDisplay: (item: any) =>{
                    return "Slide: "+ item.fields.slideNumber + " - " + item.fields.title  || "Logo";
                },
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "slideNumber",
                        displayName: "Slide Number",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
                defaultValue: [],
            },
        ],
    },
};

export default widgetModule;
